/**
 * Real Talent Pool service — mirrors the mock surface over callApi.
 *
 * All functions return ApiResponse<T> { data, error } — never throw.
 * Phase-2 endpoint map implemented here (PLAN 03-03, TAL-06).
 *
 * Auth: every function takes an Auth0 access `token` as its first argument and
 * forwards it as `Authorization: Bearer ${token}` (matching tenant.service.ts /
 * user.service.ts). Callers (the TanStack hooks) obtain the token via
 * `useAuth0().getAccessTokenSilently()`. Without it the backend returns 401 and
 * the global handler force-logs-out the user.
 */

import { callApi } from './api.service';
import type { ApiResponse } from '../models/api-response';
import type {
  TalentPoolListItem,
  TalentProfileDetail,
  TalentMatchSuggestionsPage,
  TalentSkill,
  TalentExperience,
  TalentEducation,
  TalentJobAssignment,
  TalentPoolJob,
} from '../types/talent.types';

const API_BASE = import.meta.env.VITE_API_SERVER_URL ?? '';

/** Bearer auth header, optionally with a JSON content-type for write bodies. */
const authHeaders = (token: string, json = false): Record<string, string> => ({
  Authorization: `Bearer ${token}`,
  ...(json ? { 'Content-Type': 'application/json' } : {}),
});

// ── Re-exported input interfaces (so hooks import from one place) ──────────────

export interface ListTalentsOptions {
  /** Free-text query matched against name and skill names (accent-insensitive). */
  search?: string;
  /** Filter by skill name containment (all selected skills must be present). */
  skills?: string[];
  /** Minimum years of experience. */
  yoeMin?: number;
}

export interface AddCommentInput {
  talentId: string;
  body: string;
}

export interface AssignToJobInput {
  talentId: string;
  jobId: string;
}

export interface UpdateSkillsInput {
  talentId: string;
  technicalSkills: TalentSkill[];
  softSkills: TalentSkill[];
}

export interface UpdateExperienceInput {
  talentId: string;
  experiences: TalentExperience[];
}

export interface UpdateEducationInput {
  talentId: string;
  education: TalentEducation[];
}

// ── Internal helpers ───────────────────────────────────────────────────────────

/** Shape returned by GET /api/jobs/offers — only the fields we need. */
interface PaginatedJobOffers {
  items: Array<{
    id: string;
    title: string;
    status: string;
    skills?: TalentSkill[];
    required_years_of_experience?: number;
    requires_degree?: boolean;
    business_unit?: string;
  }>;
}

// ── Query functions ─────────────────────────────────────────────────────────────

/**
 * GET /api/talent-pool/profiles page size — both the backend's default AND
 * its hard cap (`Query(100, ge=1, le=100)` in api_talent_pool.py). Keeping
 * default==max here means a tenant with <=100 talents needs exactly one
 * request; anything above that walks additional pages below. Bump both sides
 * together if this ever needs to change.
 */
const TALENT_POOL_PAGE_SIZE = 100;

/** Envelope returned by the paginated profiles endpoint. */
interface TalentPoolProfilesPage {
  items: TalentPoolListItem[];
  total: number;
  page: number;
  pages: number;
  size: number;
}

/**
 * List talents matching optional filters.
 *
 * The API is paginated (size=100, see TALENT_POOL_PAGE_SIZE above). Callers
 * (TalentPoolPage, CommandPalette) are built around a full in-memory list for
 * client search, column filters, and DataTable pagination — so this walks
 * every page and returns the complete result set. Do not drop that contract
 * without also redesigning those consumers.
 */
export const listTalents = async (
  token: string,
  opts: ListTalentsOptions = {},
): Promise<ApiResponse<TalentPoolListItem[]>> => {
  const buildParams = (page: number): URLSearchParams => {
    const params = new URLSearchParams();
    if (opts.search) params.set('search', opts.search);
    if (opts.yoeMin !== undefined) params.set('min_yoe', String(opts.yoeMin));
    if (opts.skills?.length) params.set('skills', opts.skills.join(','));
    params.set('page', String(page));
    params.set('size', String(TALENT_POOL_PAGE_SIZE));
    return params;
  };

  const fetchPage = (page: number) =>
    callApi<TalentPoolProfilesPage>(
      `${API_BASE}/api/talent-pool/profiles?${buildParams(page).toString()}`,
      { method: 'GET', headers: authHeaders(token) },
    );

  const first = await fetchPage(1);
  if (first.error || !first.data) {
    return { error: first.error };
  }

  const allItems = [...first.data.items];
  const totalPages = Math.max(1, first.data.pages);

  for (let page = 2; page <= totalPages; page += 1) {
    const next = await fetchPage(page);
    if (next.error || !next.data) {
      return { error: next.error ?? 'Failed to load talent pool page' };
    }
    allItems.push(...next.data.items);
  }

  return { data: allItems };
};

/**
 * Fetch a single talent's full profile detail.
 */
export const getTalent = async (
  token: string,
  id: string,
): Promise<ApiResponse<TalentProfileDetail>> => {
  const response = await callApi<TalentProfileDetail>(
    `${API_BASE}/api/talent-pool/profiles/${id}`,
    { method: 'GET', headers: authHeaders(token) },
  );
  return response;
};

/**
 * Download the talent's résumé PDF (generated on first request, cached backend-side).
 * Returns the PDF as a Blob; the tenant's fixed language is applied server-side.
 */
export const downloadTalentResume = async (
  token: string,
  profileId: string,
): Promise<ApiResponse<Blob>> => {
  try {
    return callApi<Blob>(
      `${API_BASE}/api/talent-pool/profiles/${profileId}/resume`,
      { method: 'GET', headers: authHeaders(token), responseType: 'blob' },
    );
  } catch (err) {
    return {
      data: undefined,
      error: err instanceof Error ? err.message : 'Failed to download resume',
    };
  }
};

/**
 * Delete a talent profile. Cascades (backend) to this tenant's candidacies and
 * their interview subtree; the global talent is pruned only when fully orphaned.
 */
export const deleteTalent = async (
  token: string,
  id: string,
): Promise<ApiResponse<void>> => {
  const response = await callApi<void>(
    `${API_BASE}/api/talent-pool/profiles/${id}`,
    { method: 'DELETE', headers: authHeaders(token) },
  );
  if (response.error) return { error: response.error };
  return { data: undefined };
};

/**
 * List all available jobs (reshapes /api/jobs/offers to TalentPoolJob[]).
 * Uses size=100 to get a practical full page; recruiter tenant-scoped.
 */
export const listJobs = async (
  token: string,
): Promise<ApiResponse<TalentPoolJob[]>> => {
  const response = await callApi<PaginatedJobOffers>(
    `${API_BASE}/api/jobs/offers?size=100`,
    { method: 'GET', headers: authHeaders(token) },
  );
  if (response.error || !response.data) {
    return { error: response.error };
  }
  const jobs: TalentPoolJob[] = response.data.items.map((j) => ({
    id: j.id,
    title: j.title,
    status: j.status,
    businessUnit: j.business_unit,
    requiredSkills: j.skills ?? [],
    requiredYoe: j.required_years_of_experience ?? 0,
    requiresDegree: j.requires_degree ?? false,
  }));
  return { data: jobs };
};

export interface MatchSuggestionsOptions {
  page?: number;
  size?: number;
  /** Free-text query matched server-side against name/title (accent-insensitive). */
  search?: string;
  /** Drop talents already assigned to this job so they are never re-offered. */
  excludeAssigned?: boolean;
}

/**
 * Fetch one page of ranked match suggestions for a job.
 * The backend scores the whole pool, ranks by score descending, then paginates —
 * so page 1 always holds the true top matches (ranking is pre-pagination). Each
 * entry embeds its score breakdown, so the caller needs no per-row follow-up call.
 */
export const listMatchSuggestions = async (
  token: string,
  jobId: string,
  opts: MatchSuggestionsOptions = {},
): Promise<ApiResponse<TalentMatchSuggestionsPage>> => {
  const params = new URLSearchParams();
  params.set('page', String(opts.page ?? 1));
  params.set('size', String(opts.size ?? 20));
  if (opts.search?.trim()) params.set('search', opts.search.trim());
  if (opts.excludeAssigned) params.set('exclude_assigned', 'true');

  const response = await callApi<TalentMatchSuggestionsPage>(
    `${API_BASE}/api/talent-pool/jobs/${jobId}/matches?${params.toString()}`,
    { method: 'GET', headers: authHeaders(token) },
  );
  if (response.error || !response.data) {
    return { error: response.error };
  }
  return { data: response.data };
};

/**
 * Return the set of talent profile IDs already assigned to a given job.
 * Calls the new tenant-scoped endpoint (TAL-06, PLAN 03-03).
 */
export const getAssignedTalentIdsForJob = async (
  token: string,
  jobId: string,
): Promise<ApiResponse<Set<string>>> => {
  const response = await callApi<string[]>(
    `${API_BASE}/api/talent-pool/jobs/${jobId}/assigned-talent-ids`,
    { method: 'GET', headers: authHeaders(token) },
  );
  if (response.error || !response.data) {
    return { error: response.error };
  }
  return { data: new Set(response.data) };
};

// ── Mutation functions ──────────────────────────────────────────────────────────

/**
 * Add a comment to a talent profile.
 */
export const addComment = async (
  token: string,
  input: AddCommentInput,
): Promise<ApiResponse<void>> => {
  const { talentId, body } = input;
  return callApi<void>(
    `${API_BASE}/api/talent-pool/profiles/${talentId}/comments`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ body }),
    },
  );
};

/**
 * Assign a talent profile to a job offer.
 * Returns the assignment response (job title, status, scores).
 */
export const assignToJob = async (
  token: string,
  input: AssignToJobInput,
): Promise<ApiResponse<TalentJobAssignment>> => {
  const { talentId, jobId } = input;
  // Response model inherits BaseSchema -> camelCase JSON matching TalentJobAssignment.
  return callApi<TalentJobAssignment>(
    `${API_BASE}/api/talent-pool/profiles/${talentId}/assign-to-job`,
    {
      method: 'POST',
      headers: authHeaders(token, true),
      body: JSON.stringify({ job_offer_id: jobId }),
    },
  );
};

/**
 * Replace the skill arrays on a talent profile (PUT /skills).
 */
export const updateSkills = async (
  token: string,
  input: UpdateSkillsInput,
): Promise<ApiResponse<void>> => {
  const { talentId, technicalSkills, softSkills } = input;
  const response = await callApi<unknown>(
    `${API_BASE}/api/talent-pool/profiles/${talentId}/skills`,
    {
      method: 'PUT',
      headers: authHeaders(token, true),
      // Backend expects List[str]; send skill names only (locale is not persisted).
      body: JSON.stringify({
        technical_skills: technicalSkills.map((s) => s.name),
        soft_skills: softSkills.map((s) => s.name),
      }),
    },
  );
  if (response.error) return { error: response.error };
  return { data: undefined };
};

/**
 * Replace a talent's experience entries via diff-and-multi-call (PLAN 03-03, Landmine 6).
 * Fetches current profile, computes diff, issues per-entry POST/PATCH/DELETE calls.
 * No bulk endpoint — the real API only has per-entry CRUD.
 */
export const updateExperience = async (
  token: string,
  input: UpdateExperienceInput,
): Promise<ApiResponse<void>> => {
  const { talentId, experiences } = input;

  // Fetch current profile to compute diff
  const profileResp = await callApi<TalentProfileDetail>(
    `${API_BASE}/api/talent-pool/profiles/${talentId}`,
    { method: 'GET', headers: authHeaders(token) },
  );
  if (profileResp.error || !profileResp.data) {
    return profileResp as ApiResponse<void>;
  }

  const currentExps = profileResp.data.experiences ?? [];
  const currentIds = new Set(currentExps.map((e) => e.id));
  const incomingIds = new Set(experiences.map((e) => e.id));

  // DELETE removed entries
  for (const exp of currentExps) {
    if (!incomingIds.has(exp.id)) {
      const resp = await callApi<unknown>(
        `${API_BASE}/api/talent-pool/profiles/${talentId}/experiences/${exp.id}`,
        { method: 'DELETE', headers: authHeaders(token) },
      );
      if (resp.error) return { error: resp.error };
    }
  }

  // POST new entries / PATCH existing ones
  for (const exp of experiences) {
    const isNew = !currentIds.has(exp.id);
    const payload = {
      role: exp.role,
      company: exp.company,
      start_date: exp.startDate,
      end_date: exp.endDate ?? null,
      is_ongoing: exp.isOngoing,
      description: exp.description ?? null,
      display_order: exp.displayOrder,
    };
    if (isNew) {
      const resp = await callApi<unknown>(
        `${API_BASE}/api/talent-pool/profiles/${talentId}/experiences`,
        {
          method: 'POST',
          headers: authHeaders(token, true),
          body: JSON.stringify(payload),
        },
      );
      if (resp.error) return { error: resp.error };
    } else {
      const resp = await callApi<unknown>(
        `${API_BASE}/api/talent-pool/profiles/${talentId}/experiences/${exp.id}`,
        {
          method: 'PATCH',
          headers: authHeaders(token, true),
          body: JSON.stringify(payload),
        },
      );
      if (resp.error) return { error: resp.error };
    }
  }

  return { data: undefined };
};

/**
 * Replace a talent's education entries via diff-and-multi-call (PLAN 03-03, Landmine 6).
 * Fetches current profile, computes diff, issues per-entry POST/PATCH/DELETE calls.
 */
export const updateEducation = async (
  token: string,
  input: UpdateEducationInput,
): Promise<ApiResponse<void>> => {
  const { talentId, education } = input;

  // Fetch current profile to compute diff
  const profileResp = await callApi<TalentProfileDetail>(
    `${API_BASE}/api/talent-pool/profiles/${talentId}`,
    { method: 'GET', headers: authHeaders(token) },
  );
  if (profileResp.error || !profileResp.data) {
    return profileResp as ApiResponse<void>;
  }

  const currentEdus = profileResp.data.education ?? [];
  const currentIds = new Set(currentEdus.map((e) => e.id));
  const incomingIds = new Set(education.map((e) => e.id));

  // DELETE removed entries
  for (const edu of currentEdus) {
    if (!incomingIds.has(edu.id)) {
      const resp = await callApi<unknown>(
        `${API_BASE}/api/talent-pool/profiles/${talentId}/education/${edu.id}`,
        { method: 'DELETE', headers: authHeaders(token) },
      );
      if (resp.error) return { error: resp.error };
    }
  }

  // POST new entries / PATCH existing ones
  for (const edu of education) {
    const isNew = !currentIds.has(edu.id);
    const payload = {
      institution: edu.institution,
      degree: edu.degree ?? null,
      field_of_study: edu.fieldOfStudy ?? null,
      start_date: edu.startDate ?? null,
      end_date: edu.endDate ?? null,
      is_ongoing: edu.isOngoing ?? false,
      graduation_year: edu.graduationYear ?? null,
      display_order: edu.displayOrder,
    };
    if (isNew) {
      const resp = await callApi<unknown>(
        `${API_BASE}/api/talent-pool/profiles/${talentId}/education`,
        {
          method: 'POST',
          headers: authHeaders(token, true),
          body: JSON.stringify(payload),
        },
      );
      if (resp.error) return { error: resp.error };
    } else {
      const resp = await callApi<unknown>(
        `${API_BASE}/api/talent-pool/profiles/${talentId}/education/${edu.id}`,
        {
          method: 'PATCH',
          headers: authHeaders(token, true),
          body: JSON.stringify(payload),
        },
      );
      if (resp.error) return { error: resp.error };
    }
  }

  return { data: undefined };
};
